import { useState, useEffect, useRef, useCallback} from "react";
import "./Courses.css";
import { useNavigate , useSearchParams  } from "react-router-dom";
import { Link } from "react-router-dom";
import { coursesApi, enrollmentsApi, wishlistApi, getToken } from "../../../api/coursesApi.js";
import Login from "../Modal/Login/Login.jsx";

/* ═══════════════════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════════════════ */
const CATEGORIES = ["All", "Development", "Data Science", "Career", "Design", "Marketing"];

const LVLCFG = {
  beginner: { label: "Beginner", clr: "#263012", bg: "rgba(38,48,18,0.08)" },
  intermediate: { label: "Intermediate", clr: "#4a5e20", bg: "rgba(74,94,32,0.09)" },
  advanced: { label: "Advanced", clr: "#263012", bg: "rgba(239,68,68,0.08)" },
  expert: { label: "Expert", clr: "#263012", bg: "rgba(122,171,46,0.10)" },
};

const fmt = (n) => (n >= 1000 ? (n / 1000).toFixed(1) + "k" : String(n));

/* ─── SVG icons (inline, no deps) ─ */
const IcoSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
const IcoClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" />
  </svg>
);
const IcoBook = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
  </svg>
);
const IcoUsers = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);
const IcoArrow = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="13" height="13">
    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
  </svg>
);
const IcoStar = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" width="11" height="11" style={{ color: "#f59e0b" }}>
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);
const IcoCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/* ─── Scroll Reveal Hook ─ */
function useReveal(thr = 0.08) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); io.disconnect(); } },
      { threshold: thr }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [thr]);
  return [ref, vis];
}

/* ─── Animated Counter ─ */
function AnimCount({ to, suffix = "" }) {
  const [val, setVal] = useState(0);
  const [ref, vis] = useReveal(0.5);
  useEffect(() => {
    if (!vis) return;
    const num = parseFloat(to);
    const isF = String(to).includes(".");
    let cur = 0, frame = 0;
    const steps = 55, inc = num / steps;
    const id = setInterval(() => {
      frame++;
      cur = Math.min(cur + inc, num);
      setVal(isF ? cur.toFixed(1) : Math.floor(cur));
      if (frame >= steps) clearInterval(id);
    }, 18);
    return () => clearInterval(id);
  }, [vis, to]);
  return <span ref={ref}>{val}{suffix}</span>;
}

/* ─── Toast ─ */
function TaToast({ msg, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className="ta-toast">
      <span className="ta-toast-dot" />
      {msg}
    </div>
  );
}

/* ─── Skeleton Card ─ */
function SkeletonCard() {
  return (
    <div className="ta-card ta-card-show" style={{ pointerEvents: "none" }}>
      <div className="ta-card-body-wrap">
        <div className="ta-thumb" style={{ background: "#eff0ec", height: 190 }}>
          <div className="ta-skeleton-shimmer" />
        </div>
        <div className="ta-content" style={{ padding: "18px 18px 12px" }}>
          <div className="ta-skel" style={{ width: "38%", height: 11, marginBottom: 12 }} />
          <div className="ta-skel" style={{ width: "92%", height: 16, marginBottom: 7 }} />
          <div className="ta-skel" style={{ width: "72%", height: 14, marginBottom: 13 }} />
          <div className="ta-skel" style={{ width: "58%", height: 11 }} />
        </div>
        <div className="ta-footer" style={{ padding: "12px 18px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div className="ta-skel" style={{ width: 32, height: 32, borderRadius: "50%" }} />
            <div>
              <div className="ta-skel" style={{ width: 78, height: 11, marginBottom: 5 }} />
              <div className="ta-skel" style={{ width: 110, height: 9 }} />
            </div>
          </div>
          <div className="ta-skel" style={{ width: 52, height: 18 }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Course Card ─ */
function TaCard({ course, delay, onEnroll, onWishlistToggle, isLoggedIn }) {
  const [ref, vis] = useReveal(0.06);
  const innerRef = useRef(null);
  const [wished, setWished] = useState(course.isWishlisted || false);
  const [enrolled, setEnrolled] = useState(course.isEnrolled || false);
  const [enrolling, setEnrolling] = useState(false);

  const lv = LVLCFG[course.level] || LVLCFG.beginner;

  const onMove = (e) => {
    const el = innerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const rx = ((e.clientX - r.left) / r.width - 0.5) * 10;
    const ry = -((e.clientY - r.top) / r.height - 0.5) * 6;
    el.style.transform = `perspective(900px) rotateY(${rx}deg) rotateX(${ry}deg) translateY(-5px) scale(1.01)`;
  };
  const onLeave = () => { if (innerRef.current) innerRef.current.style.transform = ""; };

  const handleEnroll = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) { window.dispatchEvent(new CustomEvent("open-login-drawer")); return; }
    if (enrolled) { onEnroll(course, "already_enrolled"); return; }
    setEnrolling(true);
    try {
      await enrollmentsApi.enroll(course.id);
      setEnrolled(true);
      onEnroll(course, "success");
    } catch (err) {
      onEnroll(course, "error", err.message);
    } finally {
      setEnrolling(false);
    }
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) { onWishlistToggle(null, "login_required"); return; }
    try {
      const res = await wishlistApi.toggle(course.id);
      setWished(res.wishlisted);
      onWishlistToggle(course, res.wishlisted ? "added" : "removed");
    } catch {
      onWishlistToggle(course, "error");
    }
  };

  const courseLink = `/employee/courses-main/${course.id}`;

  return (
    <div
      ref={ref}
      className={`ta-card ${vis ? "ta-card-show" : ""} ${course.featured ? "ta-card-feat" : ""}`}
      style={{ animationDelay: `${delay}ms`, "--c-acc": course.accent }}
    >
      <Link to={courseLink} style={{ textDecoration: "none", display: "block" }} tabIndex={-1}>
        <div
          ref={innerRef}
          className="ta-card-body-wrap"
          onMouseMove={onMove}
          onMouseLeave={onLeave}
          style={{ transition: "transform 0.14s ease" }}
        >
          {/* Thumbnail */}
          <div className="ta-thumb">
            <img src={course.banner} alt={course.title} className="ta-thumb-img" loading="lazy" />
            <div className="ta-thumb-veil" />
            <div className="ta-thumb-shimmer" />
            <span className="ta-cat-pill">{course.category}</span>
            {course.featured && <span className="ta-feat-pill">Featured</span>}
            {enrolled && <span className="ta-enrolled-pill"><IcoCheck /> Enrolled</span>}
            <div className="ta-lvl-bar" style={{ background: lv.clr }} />
          </div>

          {/* Content */}
          <div className="ta-content">
            <div className="ta-content-top">
              <div className="ta-stars">
                <IcoStar />
                <strong>{parseFloat(course.rating).toFixed(1)}</strong>
                <span>({fmt(course.reviews)})</span>
              </div>
              <span className="ta-lvl-pill" style={{ background: lv.bg, color: lv.clr }}>
                {lv.label}
              </span>
            </div>

            <h3 className="ta-title">{course.title}</h3>
            <p className="ta-desc">{course.description}</p>

            <div className="ta-tags">
              {(course.tags || []).map(t => <span key={t} className="ta-tag">{t}</span>)}
            </div>

            <div className="ta-meta">
              <span className="ta-meta-chip"><IcoClock /> {course.duration}</span>
              <span className="ta-meta-chip"><IcoBook /> {course.lessons} lessons</span>
              <span className="ta-meta-chip"><IcoUsers /> {fmt(course.enrolled)}</span>
            </div>
          </div>

          {/* Footer */}
          <div className="ta-footer">
            <div className="ta-inst">
              <img src={course.instructor?.avatar} alt={course.instructor?.name} className="ta-inst-ava" />
              <div>
                <div className="ta-inst-name">{course.instructor?.name}</div>
                <div className="ta-inst-role">{course.instructor?.title}</div>
              </div>
            </div>
            <div className="ta-price">
              {/* Always show original price as strikethrough */}
              {course.originalPrice > 0 && (
                <s className="ta-price-old">₹{course.originalPrice.toLocaleString()}</s>
              )}
              {/* Show FREE for price=0, otherwise show price */}
              {course.price === 0
                ? <span className="ta-price-free">FREE</span>
                : <span className="ta-price-cur" style={{ color: course.accent }}>₹{course.price.toLocaleString()}</span>
              }
            </div>
          </div>

          {/* CTA */}
          <div className="ta-cta">
            <button
              className={`ta-enroll-btn ${enrolled ? "ta-enroll-btn--done" : ""}`}
              style={{ "--btn-clr": enrolled ? "#263012" : course.accent }}
              onClick={handleEnroll}
              disabled={enrolling}
            >
              {enrolling
                ? "Enrolling…"
                : enrolled
                  ? <><IcoCheck /> Continue</>
                  : course.price === 0
                    ? <>Enroll Free <IcoArrow /></>
                    : <>Enroll Now <IcoArrow /></>
              }
            </button>
            <button
              className={`ta-wish-btn ${wished ? "ta-wish-on" : ""}`}
              onClick={handleWishlist}
              title={wished ? "Remove from wishlist" : "Save to wishlist"}
            >
              {wished ? "♥" : "♡"}
            </button>
          </div>
        </div>
      </Link>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   HERO SECTION — redesigned
═══════════════════════════════════════════════════════ */
function CoursesHero({ inputVal, setInputVal, doSearch, onKey }) {
  const QUICK_TAGS = ["React", "Python", "Machine Learning", "UI/UX Design", "AWS", "Node.js"];
  const STRIP_ITEMS = [
    "All courses free",
    "Expert instructors",
    "Certificate on completion",
    "Job-ready skills",
  ];

  return (
    <section className="ta-hero ta-hero-in" aria-label="Courses hero">
      {/* Geometric decorations */}
      <div className="ta-geo ta-geo-circle-out" />
      <div className="ta-geo ta-geo-circle-sm" />
      <div className="ta-geo ta-geo-box" />
      <div className="ta-geo ta-geo-tri" />
      <div className="ta-geo ta-geo-dotgrid">
        {Array.from({ length: 16 }).map((_, i) => <i key={i} />)}
      </div>

      <div className="ta-hero-wrap">
        {/* Badge */}
        <span className="ta-hero-badge">
          <span className="ta-badge-pulse" />
          TalentHive Learning Hub
        </span>

        {/* Headline */}
        <h1 className="ta-hero-title">
          Upskill.{" "}
          <em className="ta-hero-em">Get Hired.</em>
          <br />Grow Faster.
        </h1>

        {/* Sub */}
        <p className="ta-hero-para">
          Expert-led courses built to land your dream job —
          from coding to communication, every skill employers want.
          <strong style={{ display: "block", marginTop: 8, color: "#16a34a", fontStyle: "normal", fontSize: "15px" }}>
            All courses are completely free.
          </strong>
        </p>



        {/* Search */}
        <div className="ta-srch-outer">
          <div className="ta-srch-box">
            <span className="ta-srch-icon"><IcoSearch /></span>
            <input
              type="text"
              placeholder="Search courses, skills, topics…"
              value={inputVal}
              onChange={e => setInputVal(e.target.value)}
              onKeyDown={onKey}
              aria-label="Search courses"
            />
            <button className="ta-srch-btn" onClick={doSearch}>Search</button>
          </div>
        </div>

        {/* Quick tags */}
        <div className="ta-hero-tags">
          <span className="ta-hero-tag-label">Popular:</span>
          {QUICK_TAGS.map(tag => (
            <button
              key={tag}
              className="ta-hero-tag"
              onClick={() => { setInputVal(tag); doSearch(tag); }}
            >
              {tag}
            </button>
          ))}
        </div>
      </div>

      {/* Bottom strip */}
      <div className="ta-hero-strip">
        {STRIP_ITEMS.map((item, i) => (
          <div key={i} className="ta-hero-strip-item">
            <span className="ta-hero-strip-dot" />
            {item}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════ */
export default function CoursesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCat, setActiveCat] = useState("All");
  const [inputVal, setInputVal] = useState(searchParams.get("search") || "");
  const [search, setSearch] = useState(searchParams.get("search") || "");

  const [sort, setSort] = useState("featured");
  const [toast, setToast] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(!!getToken());
  const [retryCount, setRetryCount] = useState(0);
  const [loginOpen, setLoginOpen] = useState(false);

  /* Login drawer event */
  useEffect(() => {
    const handler = () => setLoginOpen(true);
    window.addEventListener("open-login-drawer", handler);
    return () => window.removeEventListener("open-login-drawer", handler);
  }, []);

  /* Fetch courses */
  const fetchCourses = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (activeCat !== "All") params.category = activeCat;
      if (search) params.search = search;
      if (sort) params.sort = sort;
      const data = await coursesApi.getAll(params);
      setCourses(data.courses || []);
    } catch (err) {
      setError(err.message || "Failed to load courses. Is the server running?");
    } finally {
      setLoading(false);
    }
  }, [activeCat, search, sort, retryCount]); // eslint-disable-line

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  /* Search helpers */
  const doSearch = useCallback((forceTerm) => {
    const term = typeof forceTerm === "string" ? forceTerm : inputVal.trim();
    setSearch(term);
  }, [inputVal]);

  const onKey = (e) => { if (e.key === "Enter") doSearch(); };

  const showToast = (msg) => setToast(msg);

  const handleEnroll = (course, status, errMsg) => {
    if (status === "already_enrolled") {
      navigate(`/employee/courses-main/${course.id}`);
      return;
    }
    if (status === "error") {
      const msg = String(errMsg || "");
      if (msg.toLowerCase().includes("unauthorized") || msg.toLowerCase().includes("token")) setLoginOpen(true);
      showToast(`❌ ${errMsg || "Enrollment failed"}`);
      return;
    }
    showToast(`🎉 Enrolled in "${course.title.split(":")[0]}"!`);
    setTimeout(() => navigate(`/employee/courses-main/${course.id}`), 1200);
  };

  const handleWishlistToggle = (course, status) => {
    if (status === "login_required") { showToast("🔒 Please log in to save courses"); return; }
    if (status === "error") { showToast("❌ Failed to update wishlist"); return; }
    if (status === "added") showToast(`💾 Saved "${course?.title?.split(":")[0]}"`);
    if (status === "removed") showToast("Removed from wishlist");
  };

  const featList = courses.filter(c => c.featured);
  const restList = courses.filter(c => !c.featured);

  /* Error state */
  if (error) return (
    <div className="ta-page" style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "60vh" }}>
      <div style={{ textAlign: "center", maxWidth: 420, padding: 32 }}>
        <div style={{ fontSize: "3rem", marginBottom: 16 }}>⚠️</div>
        <h2 style={{ fontSize: "1.25rem", fontWeight: 800, color: "#1a1f0e", marginBottom: 8, fontFamily: "'DM Sans', sans-serif" }}>
          Could not load courses
        </h2>
        <p style={{ color: "#6b7a50", fontSize: "0.88rem", marginBottom: 24, lineHeight: 1.7 }}>{error}</p>
        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
          <button
            onClick={() => setRetryCount(n => n + 1)}
            style={{ background: "#263012", color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem", fontFamily: "'DM Sans', sans-serif" }}>
            Retry
          </button>
          <button
            onClick={() => window.location.href = "/"}
            style={{ background: "#f2f5ea", color: "#263012", border: "1px solid rgba(38,48,18,0.15)", borderRadius: 10, padding: "11px 24px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem", fontFamily: "'DM Sans', sans-serif" }}>
            Go Home
          </button>
        </div>
        <p style={{ marginTop: 20, fontSize: "0.74rem", color: "#9aab80" }}>
          Make sure your server is running: <code style={{ background: "#f2f5ea", padding: "2px 7px", borderRadius: 5, fontFamily: "'IBM Plex Mono', monospace" }}>node server.js</code>
        </p>
      </div>
    </div>
  );

  return (
    <div className="ta-page">
      {/* Background */}
      <div className="ta-bg" aria-hidden="true">
        <div className="ta-blob ta-blob-1" />
        <div className="ta-blob ta-blob-2" />
        <div className="ta-blob ta-blob-3" />
      </div>

      {/* Hero */}
      <CoursesHero
        inputVal={inputVal}
        setInputVal={setInputVal}
        doSearch={doSearch}
        onKey={onKey}
      />

      {/* Controls */}
      <div className="ta-ctrl-bar">
        <div className="ta-pill-row">
          {CATEGORIES.map(cat => (
            <button
              key={cat}
              className={`ta-pill ${activeCat === cat ? "ta-pill-on" : ""}`}
              onClick={() => setActiveCat(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
        <select className="ta-sort-box" value={sort} onChange={e => setSort(e.target.value)}>
          <option value="featured">Featured First</option>
          <option value="rating">Top Rated</option>
          <option value="popular">Most Popular</option>
          <option value="price-low">Price: Low → High</option>
          <option value="price-high">Price: High → Low</option>
        </select>
      </div>

      {/* Loading */}
      {loading && (
        <>
          <div className="ta-divider">
            <span className="ta-divider-lbl">Featured Courses</span>
            <div className="ta-divider-line" />
          </div>
          <div className="ta-grid">
            {Array.from({ length: 3 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
          <div className="ta-divider">
            <span className="ta-divider-lbl">All Courses</span>
            <div className="ta-divider-line" />
          </div>
          <div className="ta-grid">
            {Array.from({ length: 5 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        </>
      )}

      {/* Featured */}
      {!loading && featList.length > 0 && (
        <>
          <div className="ta-divider">
            <span className="ta-divider-lbl">Featured Courses</span>
            <div className="ta-divider-line" />
          </div>
          <div className="ta-grid">
            {featList.map((c, i) => (
              <TaCard key={c.id} course={c} delay={i * 75}
                onEnroll={handleEnroll}
                onWishlistToggle={handleWishlistToggle}
                isLoggedIn={isLoggedIn}
              />
            ))}
          </div>
        </>
      )}

      {/* All */}
      {!loading && restList.length > 0 && (
        <>
          <div className="ta-divider">
            <span className="ta-divider-lbl">All Courses</span>
            <div className="ta-divider-line" />
          </div>
          <div className="ta-grid">
            {restList.map((c, i) => (
              <TaCard key={c.id} course={c} delay={i * 60}
                onEnroll={handleEnroll}
                onWishlistToggle={handleWishlistToggle}
                isLoggedIn={isLoggedIn}
              />
            ))}
          </div>
        </>
      )}

      {/* Empty */}
      {!loading && courses.length === 0 && !error && (
        <div className="ta-no-results">
          <div className="ta-no-icon">🔍</div>
          <div className="ta-no-title">No courses found</div>
          <div className="ta-no-sub">Try a different search term or category</div>
          <button
            onClick={() => { setActiveCat("All"); setSearch(""); setInputVal(""); }}
            style={{ marginTop: 18, background: "#263012", color: "#fff", border: "none", borderRadius: 10, padding: "11px 24px", fontWeight: 700, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontSize: "0.88rem" }}>
            Clear filters
          </button>
        </div>
      )}

      {toast && <TaToast msg={toast} onClose={() => setToast(null)} />}

      <Login
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoginSuccess={({ token, user }) => {
          setLoginOpen(false);
          setIsLoggedIn(true);
        }}
      />
    </div>
  );
}